<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import "@maersk-global/mds-components-core/mc-checkbox-group";
import "@maersk-global/mds-components-core/mc-checkbox";
import { useFiltersStore } from "@/store";
import { useApplyFilter } from "@/features/filters/composables/useApplyFilter/useApplyFilter";
import type { FilterDefinition, FilterSelection, FilterType } from "@/features/filters/types";
import { FilterTypeEnum } from "@/features/filters/types";

const props = defineProps<{
  filter: FilterDefinition;
  filterType: FilterType;
  zonePrefix?: string;
  disabled?: boolean;
}>();

const { t } = useI18n();
const filtersStore = useFiltersStore();
const { filtersApplied, panelSelectedFilters } = filtersStore;
const { applyFilter, removeFilter } = useApplyFilter(props.filterType, props.zonePrefix);

const zone = props.zonePrefix || "default";
const { key: filterKey, label: filterLabel = "", groupId, groupLabel, sectionLabel } = props.filter;

const stored = computed(() => {
  const source = props.filterType === FilterTypeEnum.PANEL_FILTERS ? panelSelectedFilters : filtersApplied;
  return source?.[zone]?.[filterKey];
});

const selectedValues = computed<FilterSelection[]>(() => {
  const value = stored.value?.value;
  return Array.isArray(value) ? value : [];
});

const options = computed(() => props.filter.options ?? []);

const isChecked = (value: string | number) => selectedValues.value.some((s) => s.value === value);

const onChange = (option: { label: string; value: string | number }, checked: boolean) => {
  const current = selectedValues.value.filter((s) => s.value !== option.value);
  const next = checked ? [...current, { label: t(option.label), value: option.value }] : current;
  // Keep the options' declared order so the payload is stable regardless of click order.
  const ordered = options.value
    .map((o) => next.find((s) => s.value === o.value))
    .filter((s): s is FilterSelection => Boolean(s));

  if (!ordered.length) {
    removeFilter(filterKey);
    return;
  }
  applyFilter(filterKey, {
    filterKey,
    value: ordered,
    filterType: props.filterType,
    ...(groupId ? { groupId } : {}),
    ...(groupLabel ? { groupLabel } : {}),
    ...(sectionLabel ? { sectionLabel } : {}),
    ...(filterLabel ? { filterLabel } : {})
  });
};
</script>

<template>
  <mc-checkbox-group
    class="checkbox-group-filter"
    fit="small"
    :legend="t(filterLabel)"
    :hiddenlegend="filter.hiddenlabel || undefined"
    :name="filterKey"
    :data-test="`checkbox-group-filter/${filterKey}`"
  >
    <mc-checkbox
      v-for="option in options"
      :key="option.value"
      :value="option.value"
      :label="t(option.label)"
      :checked="isChecked(option.value) || undefined"
      :disabled="disabled || option.disabled || undefined"
      :data-test="`checkbox-group-filter/${filterKey}/${option.value}`"
      @change="(e: Event) => onChange(option, (e.target as HTMLInputElement).checked)"
    />
  </mc-checkbox-group>
</template>
